import {
  TiffError, decodeTiff, isTiff, type TiffInflate, type TiffOptions, type TiffPicture,
} from './tiff';

/**
 * Which kind of picture a file is, from its first bytes rather than its name — a scan saved as
 * `.jpg` that is really a TIFF is read as the TIFF it is — and opening the kinds the browser does
 * not open itself.
 */

export type PictureFormat = 'png' | 'jpeg' | 'gif' | 'webp' | 'bmp' | 'tiff' | 'heic' | 'avif' | 'svg' | 'ico' | 'pdf' | 'unknown';

/** The type each kind is served as: what an image element needs to be told, an SVG above all. */
export const PICTURE_TYPES: Partial<Record<PictureFormat, string>> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  tiff: 'image/tiff',
  heic: 'image/heic',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
};

/** What the file input offers: every picture, and the kinds some systems do not call pictures. */
export const PICTURE_ACCEPT = 'image/*,.tif,.tiff,.heic,.heif,.avif,.bmp,.svg';

const startsWith = (bytes: Uint8Array, prefix: readonly number[], at = 0): boolean => prefix
  .every((byte, index) => bytes[at + index] === byte);
const textOf = (bytes: Uint8Array, from: number, to: number): string => String.fromCharCode(...Array.from(bytes.subarray(from, to)));

const AVIF_BRANDS = ['avif', 'avis'];
const HEIC_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis'];

/**
 * Whether an ISO media file is an AVIF or a HEIC picture, by its brands: the major one, and when
 * that is only the general `mif1` or `msf1`, the compatible ones listed after it in the `ftyp` box.
 */
const heifKindOf = (bytes: Uint8Array): PictureFormat => {
  const major = textOf(bytes, 8, 12);
  if (AVIF_BRANDS.includes(major)) return 'avif';
  if (HEIC_BRANDS.includes(major)) return 'heic';
  if (major !== 'mif1' && major !== 'msf1') return 'unknown';
  // The box's size first, then the major brand, its version, and four bytes each compatible brand.
  const size = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0);
  const end = Math.min(bytes.length, size >= 16 ? size : bytes.length);
  const compatible: string[] = [];
  for (let at = 16; at + 4 <= end; at += 4) compatible.push(textOf(bytes, at, at + 4));
  if (compatible.some((brand) => AVIF_BRANDS.includes(brand))) return 'avif';
  return 'heic';
};

export const pictureFormatOf = (bytes: Uint8Array): PictureFormat => {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47])) return 'png';
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (textOf(bytes, 0, 4) === 'GIF8') return 'gif';
  if (textOf(bytes, 0, 4) === 'RIFF' && textOf(bytes, 8, 12) === 'WEBP') return 'webp';
  if (textOf(bytes, 0, 2) === 'BM') return 'bmp';
  if (isTiff(bytes)) return 'tiff';
  if (textOf(bytes, 0, 4) === '%PDF') return 'pdf';
  if (startsWith(bytes, [0, 0, 1, 0])) return 'ico';
  if (textOf(bytes, 4, 8) === 'ftyp') return heifKindOf(bytes);
  const start = textOf(bytes, 0, Math.min(bytes.length, 512)).replace(/^\uFEFF/, '').trimStart().toLowerCase();
  if (start.startsWith('<svg') || (start.startsWith('<?xml') && start.includes('<svg'))) return 'svg';
  return 'unknown';
};

/**
 * Inflating with the browser's own `DecompressionStream`, for a TIFF compressed with Deflate: read
 * as it comes, and stopped once the `expected` bytes of the strip or tile are there, so a strip
 * that would inflate to far more cannot fill the memory. A strip cut short, or with something
 * after its data, gives what did inflate, the rest left 0, rather than failing the whole picture.
 */
export const inflateInBrowser: TiffInflate = async (data, expected) => {
  // Not in this TypeScript's list of browser types yet, though every current browser has it.
  const Decompression = (globalThis as unknown as {
    DecompressionStream?: new (format: 'deflate') => TransformStream<Uint8Array, Uint8Array>;
  }).DecompressionStream;
  if (!Decompression) throw new TiffError('this browser cannot read Deflate-compressed TIFF files');

  const stream = new Decompression('deflate');
  const writer = stream.writable.getWriter();
  // Their failures are the reader's too, and are dealt with there.
  writer.write(data).catch(() => undefined);
  writer.close().catch(() => undefined);
  const reader = stream.readable.getReader();
  const out = new Uint8Array(expected);
  let written = 0;

  try {
    while (written < expected) {
      // eslint-disable-next-line no-await-in-loop -- the inflated bytes come a piece at a time
      const { done, value } = await reader.read();
      if (done) break;
      out.set(value.subarray(0, expected - written), written);
      written += Math.min(value.length, expected - written);
    }
  } catch {
    // Damaged or cut short: what inflated so far is what there is.
  } finally {
    reader.cancel().catch(() => undefined);
  }

  return out;
};

/** A file's bytes: by `arrayBuffer` where a browser has it, by a `FileReader` where it is older. */
export const bytesOf = async (file: Blob): Promise<Uint8Array> => {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = (): void => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

/** A TIFF file's first page, read here: no browser but Safari opens one itself. */
export const tiffOf = async (file: Blob, inflate: TiffInflate = inflateInBrowser, options: TiffOptions = {}): Promise<TiffPicture> => (
  decodeTiff(await bytesOf(file), inflate, options));

/** The first bytes of a file: enough to tell what it is. */
export const headOf = (file: Blob): Promise<Uint8Array> => bytesOf(file.slice(0, 512));

/**
 * Why a picture could not be opened, as a translation key: a HEIC photo in a browser that cannot
 * open one, a PDF that is not a picture at all, or a file that is no picture.
 */
export const unreadableKeyOf = (format: PictureFormat): string => {
  if (format === 'heic') return 'floorplan-editor-image-unreadable-heic';
  if (format === 'pdf') return 'floorplan-editor-image-unreadable-pdf';
  if (format === 'tiff') return 'floorplan-editor-image-unreadable-tiff';
  return 'floorplan-editor-image-unreadable';
};
